import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import partnersLogo from "@/assets/partners_logo.png";

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

export const ResetPasswordPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>();

  const password = watch("password");

  useEffect(() => {
    // Check if we have a valid session (user clicked the reset link)
    const checkSession = async () => {
      // Supabase can redirect with either hash fragments or query parameters
      // Handle hash fragments first (most common)
      const hash = window.location.hash;
      const searchParams = new URLSearchParams(window.location.search);
      
      let accessToken: string | null = null;
      let refreshToken: string | null = null;
      let type: string | null = null;
      
      if (hash) {
        // Extract from hash fragments
        const hashParams = new URLSearchParams(hash.substring(1));
        accessToken = hashParams.get('access_token');
        refreshToken = hashParams.get('refresh_token');
        type = hashParams.get('type');
      } else if (searchParams.has('access_token')) {
        // Extract from query parameters (when Gmail wraps the URL)
        accessToken = searchParams.get('access_token');
        refreshToken = searchParams.get('refresh_token');
        type = searchParams.get('type');
      }
      
      if (accessToken && type === 'recovery') {
        try {
          // Set the session from the token
          const { data: { session }, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });
          
          if (session && !error) {
            setIsValidToken(true);
            // Clean up the URL - remove hash and query params
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            console.error("Session error:", error);
            setIsValidToken(false);
          }
        } catch (error) {
          console.error("Error setting session:", error);
          setIsValidToken(false);
        }
      } else {
        // Check existing session (user might have already authenticated)
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
        }
      }
    };

    checkSession();
  }, []);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (data.password !== data.confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    setIsLoading(true);
    try {
      await updatePassword(data.password);
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => {
        // Redirect to dashboard since user is already authenticated
        navigate("/dashboard");
      }, 2000);
    } catch (error: unknown) {
      console.error("Reset password error:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao redefinir senha. Tente novamente.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidToken === null) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando link...</p>
        </div>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 sm:p-6 lg:p-8 font-sans">
        <div className="w-full max-w-md mx-auto">
          <Card className="w-full shadow-xl border-0">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl sm:text-3xl font-bold">Link Inválido</CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Este link de recuperação é inválido ou expirou.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <Button onClick={() => navigate("/forgot-password")} className="w-full">
                Solicitar Novo Link
              </Button>
              <div className="mt-4 text-center">
                <Button variant="link" onClick={() => navigate("/")} className="p-0 h-auto">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Voltar ao Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 sm:p-6 lg:p-8 font-sans">
      <div className="w-full max-w-md lg:max-w-lg xl:max-w-xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <Card className="mb-4 sm:mb-6 shadow-lg">
            <CardContent className="pt-6 pb-4 sm:pb-6">
              <img src={partnersLogo} alt="Silva Partners" className="mx-auto h-16 sm:h-20 mb-4" />
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">Silva Partners</h1>
            </CardContent>
          </Card>
        </div>

        <Card className="w-full shadow-xl border-0">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl sm:text-3xl font-bold">Redefinir Senha</CardTitle>
            <CardDescription className="text-sm sm:text-base">Digite sua nova senha abaixo</CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Nova Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    {...register("password", {
                      required: "Senha é obrigatória",
                      minLength: {
                        value: 6,
                        message: "Senha deve ter pelo menos 6 caracteres",
                      },
                    })}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className="pl-10 pr-12 h-11 sm:h-12"
                    placeholder="••••••••"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-9 w-9 p-0 hover:bg-muted"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {errors.password && <p className="text-xs sm:text-sm text-destructive">{errors.password.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirmar Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    {...register("confirmPassword", {
                      required: "Confirmação de senha é obrigatória",
                      validate: (value) => value === password || "As senhas não coincidem",
                    })}
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    className="pl-10 pr-12 h-11 sm:h-12"
                    placeholder="••••••••"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-9 w-9 p-0 hover:bg-muted"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs sm:text-sm text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>

              <Button type="submit" disabled={isLoading} className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium">
                {isLoading ? "Redefinindo..." : "Redefinir Senha"}
              </Button>
            </form>

            <div className="mt-4 sm:mt-6 text-center">
              <Button variant="link" onClick={() => navigate("/")} className="p-0 h-auto font-medium text-primary hover:text-primary/80">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Voltar ao Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

